import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { LoadingOverlay } from "../../../components/LoadingOverlay/LoadingOverlay";
import { useOrganizationContext } from "../../../contexts/organizations/organizationsContext";
import { useKeys } from "../../../hooks/reactQueryHooks/useKeys";
import type { Mentor } from "../../../hooks/reactQueryHooks/useMentors";
import { Controls } from "./components";
import { ErrorWebRTC } from "./ErrorWebRTC";
import { useMicrophones } from "./hooks/useMicrophones";
import type { IUseWebRTCProps } from "./hooks/useWebRTC";
import { useWebRTC } from "./hooks/useWebRTC";

interface SessionPageProps {
  mentor: Mentor;
}

export interface SessionPageRef {
  closeConnection?: () => void;
}

export const SessionPage = forwardRef<SessionPageRef, SessionPageProps>(
  ({ mentor }: SessionPageProps, ref) => {
    const { state } = useLocation();
    const { currentOrganization } = useOrganizationContext();
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [avatarReady, setAvatarReady] = useState(false);
    const [isAvatarTalking, setIsAvatarTalking] = useState(false);

    useImperativeHandle(ref, () => ({
      closeConnection,
    }));

    const { data: keysList } = useKeys({
      organizationId: currentOrganization?.id || "",
      type: "proxy",
      pageSize: 100,
      isEnabled: !(state as { apiKey?: string })?.apiKey,
    });

    const handleMessage: IUseWebRTCProps["onMessage"] = data => {
      switch (data.event) {
        case "avatar_state_changed":
          if (data.data?.state === "ready") {
            setAvatarReady(true);
          }
          break;
        case "talk_state_changed":
          setIsAvatarTalking(
            data.data?.state !== "ended" && data.data?.state !== "canceled",
          );
          break;
        default:
          break;
      }
    };

    const {
      isMuted,
      stream,
      selectedMicrophone,
      connectMicrophone,
      muteMicrophone,
    } = useMicrophones();

    const {
      videoStream,
      audioStream,
      dataChannel,
      closeConnection,
      sessionId,
      isLoading,
      error,
    } = useWebRTC({
      mentorId: mentor.mentorId,
      userMicrophoneStream: stream,
      selectedMicrophone,
      onMessage: handleMessage,
      apiKey:
        (state as { apiKey?: string })?.apiKey || keysList?.items[0]?.key || "",
    });

    useEffect(() => {
      void connectMicrophone();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (videoRef.current && videoStream) {
        videoRef.current.srcObject = videoStream;
      }
    }, [videoStream, avatarReady]);

    useEffect(() => {
      if (audioRef.current && audioStream) {
        audioRef.current.srcObject = audioStream;
      }
    }, [audioStream, avatarReady]);

    const handleStop = () => {
      const data = {
        type: "cancel",
        data: {
          sessionId: sessionId,
        },
      };
      console.log("stop avatar", data);
      dataChannel?.send?.(JSON.stringify(data));
    };

    if (error) {
      return <ErrorWebRTC />;
    }

    return (
      <>
        <div className="session-container">
          {/* Video element for remote video */}
          {avatarReady && videoStream && (
            <video
              ref={videoRef}
              role="video"
              autoPlay
              playsInline
              muted
              className="session-video"
            />
          )}

          {/* Audio element for remote audio */}
          {avatarReady && audioStream && (
            <audio
              ref={audioRef}
              role="audio"
              autoPlay
              className="session-audio"
            />
          )}

          {/* Mute/Unmute button */}
          {avatarReady && !!sessionId && (
            <div className="session-controls">
              <Controls
                isAvatarSpeaking={isAvatarTalking}
                isMuted={isMuted}
                toggleMute={() => muteMicrophone(!isMuted)}
                selectedMicrophone={selectedMicrophone}
                onStopTalking={handleStop}
                handleEnd={() => {
                  closeConnection();
                  window.history.back();
                }}
              />
            </div>
          )}
        </div>
        {(isLoading || !avatarReady) && (
          <div className="spinner-container">
            <LoadingOverlay />
          </div>
        )}
      </>
    );
  },
);
